<template>
  <div class="gestion-pago">
    <div class="cabecera">
      <h2 class="titulo">
        <i class="bi bi-credit-card-2-front me-2"></i>Gestion de pago
      </h2>
      <span :class="['badge-estado', `estado-${estado}`]">{{ estadoLabel }}</span>
    </div>

    <div v-if="estado === 'vencida'" class="alerta-vencida">
      <i class="bi bi-exclamation-octagon-fill me-2"></i>
      <span>
        Tu suscripcion esta vencida. El sistema esta bloqueado.
        Sube el comprobante de pago para restablecer el acceso.
      </span>
    </div>

    <div v-else-if="estado === 'en_gracia'" class="alerta-gracia">
      <i class="bi bi-clock-history me-2"></i>
      <span>
        Estas en periodo de gracia.
        Regulariza el pago antes de la suspension del servicio.
      </span>
    </div>

    <div v-if="estado === 'cancelada'" class="cancelada-card">
      <i class="bi bi-x-octagon-fill cancelada-icono"></i>
      <h3>Suscripcion cancelada</h3>
      <p>
        Tu suscripcion fue cancelada. Para reactivar el servicio,
        contacta al administrador del sistema.
      </p>
      <router-link to="/cerrar-sesion" class="btn-cerrar-sesion">
        <i class="bi bi-box-arrow-right me-2"></i>Cerrar sesion
      </router-link>
    </div>

    <form v-else class="form-card" @submit.prevent="enviar">
      <h4 class="form-titulo">Subir comprobante</h4>

      <!-- Contador de intentos -->
      <div :class="['intentos-info', intentosRestantes === 0 ? 'intentos-agotados' : '']">
        <i class="bi bi-info-circle me-1"></i>
        Intentos usados: <strong>{{ intentosUsados }} / 3</strong>
        <span v-if="intentosRestantes > 0"> &mdash; Te quedan {{ intentosRestantes }} intento(s).</span>
        <span v-else> &mdash; No tienes mas intentos. Contacta al administrador.</span>
      </div>

      <template v-if="intentosRestantes > 0">
        <label class="campo-label mt-3">Banco o cooperativa <span class="req">*</span></label>
        <input
          v-model="banco"
          type="text"
          class="form-control"
          placeholder="Ej: Banco Pichincha, Cooperativa JEP"
          maxlength="150"
          :disabled="enviando"
          required
        />

        <label class="campo-label mt-3">Numero de comprobante / referencia <span class="req">*</span></label>
        <input
          v-model="numeroComprobante"
          type="text"
          class="form-control"
          placeholder="Ej: 00123456789"
          maxlength="100"
          :disabled="enviando"
          required
        />

        <label class="campo-label mt-3">Fecha de transferencia <span class="req">*</span></label>
        <input
          v-model="fechaTransferencia"
          type="date"
          class="form-control"
          :disabled="enviando"
          required
        />

        <label class="campo-label mt-3">Archivo comprobante (PDF, JPG o PNG, max 5MB) <span class="req">*</span></label>
        <input
          ref="archivoInput"
          type="file"
          class="form-control"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          :disabled="enviando"
          @change="onArchivo"
        />

        <label class="campo-label mt-3">Nota adicional (opcional)</label>
        <textarea
          v-model="nota"
          class="form-control"
          rows="2"
          maxlength="500"
          placeholder="Informacion adicional"
          :disabled="enviando"
        ></textarea>

        <div v-if="error" class="alert-error">{{ error }}</div>
        <div v-if="exito" class="alert-ok">
          <i class="bi bi-check-circle me-1"></i>
          Comprobante enviado. Tienes 24 horas de gracia mientras el administrador lo valida.
        </div>

        <button type="submit" class="btn-enviar" :disabled="!puedeEnviar">
          <span v-if="enviando"><i class="bi bi-hourglass-split me-2"></i>Enviando...</span>
          <span v-else><i class="bi bi-cloud-upload me-2"></i>Enviar comprobante</span>
        </button>
      </template>
    </form>

    <div v-if="comprobantes.length && estado !== 'cancelada'" class="historial">
      <h4 class="form-titulo">Comprobantes enviados</h4>
      <table class="tabla">
        <thead>
          <tr>
            <th>Archivo</th>
            <th>Banco</th>
            <th>N. Comprobante</th>
            <th>Fecha transf.</th>
            <th>Estado</th>
            <th>Gracia expira</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in comprobantes" :key="c.id">
            <td class="archivo-cell">{{ c.archivo_nombre }}</td>
            <td>{{ c.banco || '-' }}</td>
            <td>{{ c.numero_comprobante || '-' }}</td>
            <td>{{ c.fecha_transferencia || '-' }}</td>
            <td>
              <span :class="['badge-estado', `estado-${c.estado}`]">{{ c.estado }}</span>
              <div v-if="c.motivo_rechazo" class="motivo-rechazo">{{ c.motivo_rechazo }}</div>
            </td>
            <td>{{ c.gracia_expira ? formatFecha(c.gracia_expira) : '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'GestionPago',
  data() {
    return {
      archivo: null,
      banco: '',
      numeroComprobante: '',
      fechaTransferencia: '',
      nota: '',
      enviando: false,
      error: '',
      exito: false,
      comprobantes: [],
      intentosUsados: 0,
      intentosRestantes: 3,
    }
  },
  computed: {
    estado() {
      return this.$store.getters.subscriptionEstado || ''
    },
    estadoLabel() {
      const m = {
        activa: 'Activa',
        en_gracia: 'En gracia',
        vencida: 'Vencida',
        cancelada: 'Cancelada'
      }
      return m[this.estado] || 'Sin suscripcion'
    },
    puedeEnviar() {
      return !this.enviando
        && !!this.archivo
        && this.banco.trim() !== ''
        && this.numeroComprobante.trim() !== ''
        && this.fechaTransferencia !== ''
    }
  },
  mounted() {
    this.cargarHistorial()
  },
  methods: {
    onArchivo(event) {
      const archivos = event.target.files || []
      this.archivo = archivos.length ? archivos[0] : null
      this.error = ''
      this.exito = false
    },
    async cargarHistorial() {
      try {
        const { data } = await api.get('/suscripcion/comprobantes')
        this.comprobantes      = Array.isArray(data?.data) ? data.data : []
        this.intentosUsados    = data?.intentos_usados    ?? 0
        this.intentosRestantes = data?.intentos_restantes ?? 3
      } catch (e) {
        this.comprobantes = []
      }
    },
    async enviar() {
      if (!this.archivo) {
        this.error = 'Selecciona un archivo.'
        return
      }
      if (!this.banco.trim()) {
        this.error = 'Ingresa el banco o cooperativa.'
        return
      }
      if (!this.numeroComprobante.trim()) {
        this.error = 'Ingresa el numero de comprobante.'
        return
      }
      if (!this.fechaTransferencia) {
        this.error = 'Ingresa la fecha de transferencia.'
        return
      }
      if (this.archivo.size > 5 * 1024 * 1024) {
        this.error = 'El archivo supera 5 MB.'
        return
      }
      this.error = ''
      this.exito = false
      this.enviando = true
      try {
        const form = new FormData()
        form.append('comprobante', this.archivo)
        form.append('banco', this.banco.trim())
        form.append('numero_comprobante', this.numeroComprobante.trim())
        form.append('fecha_transferencia', this.fechaTransferencia)
        if (this.nota) form.append('nota', this.nota)
        await api.post('/suscripcion/comprobante', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.exito = true
        this.archivo = null
        this.banco = ''
        this.numeroComprobante = ''
        this.fechaTransferencia = ''
        this.nota = ''
        if (this.$refs.archivoInput) this.$refs.archivoInput.value = ''
        await this.cargarHistorial()
        // Refrescar permisos para que el store refleje el estado en_gracia
        await this.$store.dispatch('cargarPermisosEfectivos')
      } catch (e) {
        this.error = e?.response?.data?.error || 'No se pudo enviar el comprobante.'
      } finally {
        this.enviando = false
      }
    },
    formatFecha(dt) {
      if (!dt) return '-'
      return dt.replace('T', ' ').substring(0, 16)
    }
  }
}
</script>

<style scoped>
.gestion-pago {
  max-width: 760px;
  margin: 0 auto;
}

.cabecera {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.titulo {
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.alerta-vencida,
.alerta-gracia {
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
}

.alerta-vencida {
  background: #f8d7da;
  color: #842029;
  border: 1px solid #f5c2c7;
}

.alerta-gracia {
  background: #fff3cd;
  color: #664d03;
  border: 1px solid #ffe69c;
}

.form-card,
.historial {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
}

.dark-theme .form-card,
.dark-theme .historial {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.form-titulo {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.campo-label {
  font-size: 0.9rem;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
}

.form-control {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.95rem;
}

.btn-enviar {
  margin-top: 14px;
  background: #198754;
  color: white;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
}

.btn-enviar:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.alert-error {
  margin-top: 12px;
  padding: 8px 10px;
  background: #f8d7da;
  color: #842029;
  border-radius: 6px;
  font-size: 0.9rem;
}

.alert-ok {
  margin-top: 12px;
  padding: 8px 10px;
  background: #d1e7dd;
  color: #0f5132;
  border-radius: 6px;
  font-size: 0.9rem;
}

.tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.tabla th,
.tabla td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.tabla th {
  background: #f8f9fa;
  font-weight: 600;
}

.dark-theme .tabla th {
  background: #1a202c;
}

.dark-theme .tabla td,
.dark-theme .tabla th {
  border-color: #4a5568;
}

.archivo-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nota-cell {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge-estado {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
}

.cancelada-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 30px 24px;
  text-align: center;
}

.dark-theme .cancelada-card {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.cancelada-icono {
  font-size: 3rem;
  color: #dc3545;
  display: block;
  margin-bottom: 8px;
}

.cancelada-card h3 {
  margin: 0 0 8px;
  font-size: 1.2rem;
  color: #842029;
}

.cancelada-card p {
  color: #555;
  margin-bottom: 18px;
}

.dark-theme .cancelada-card p { color: #cbd5e0; }

.btn-cerrar-sesion {
  display: inline-flex;
  align-items: center;
  background: #6c757d;
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
}

.btn-cerrar-sesion:hover {
  background: #5a6268;
  color: white;
}

.estado-activa     { background: #d1e7dd; color: #0f5132; }
.estado-en_gracia  { background: #fff3cd; color: #664d03; }
.estado-vencida    { background: #f8d7da; color: #842029; }
.estado-cancelada  { background: #e9ecef; color: #495057; }
.estado-pendiente  { background: #fff3cd; color: #664d03; }
.estado-aprobado   { background: #d1e7dd; color: #0f5132; }
.estado-rechazado  { background: #f8d7da; color: #842029; }

.intentos-info {
  padding: 8px 12px;
  background: #e0f2fe;
  color: #0c4a6e;
  border-radius: 6px;
  font-size: 0.88rem;
  margin-bottom: 6px;
}

.intentos-agotados {
  background: #f8d7da;
  color: #842029;
}

.req {
  color: #dc3545;
}

.motivo-rechazo {
  font-size: 0.8rem;
  color: #842029;
  margin-top: 3px;
}
</style>
